import { type DragEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Brain, Code, Globe, Bot } from 'lucide-react'
import type { NodeType } from '@/schemas/workflow-node'
import { nodeTypeSchema } from '@/schemas/workflow-node'

interface PaletteItemProps {
  type: NodeType
  label: string
  icon: ReactNode
  color: string
}

function PaletteItem({ type, label, icon, color }: PaletteItemProps) {
  const onDragStart = (e: DragEvent<HTMLDivElement>) => {
    e.dataTransfer.setData('application/reactflow', type)
    e.dataTransfer.effectAllowed = 'move'
  }

  return (
    <div
      className={cn(
        'flex items-center gap-2 p-2 rounded-lg border border-border bg-card cursor-grab hover:bg-secondary',
        `border-l-4 ${color}`
      )}
      onDragStart={onDragStart}
      draggable
    >
      {icon}
      <span className="text-sm font-semibold">{label}</span>
    </div>
  )
}

const paletteItems: {
  type: NodeType
  icon: ReactNode
  color: string
  labelKey: string
}[] = [
  {
    type: 'llm',
    icon: <Brain className="w-4 h-4 text-indigo-500" />,
    color: 'border-l-indigo-500',
    labelKey: 'flow.nodeTypes.llm',
  },
  {
    type: 'code',
    icon: <Code className="w-4 h-4 text-emerald-500" />,
    color: 'border-l-emerald-500',
    labelKey: 'flow.nodeTypes.code',
  },
  {
    type: 'http',
    icon: <Globe className="w-4 h-4 text-amber-500" />,
    color: 'border-l-amber-500',
    labelKey: 'flow.nodeTypes.http',
  },
  {
    type: 'agent',
    icon: <Bot className="w-4 h-4 text-purple-500" />,
    color: 'border-l-purple-500',
    labelKey: 'flow.nodeTypes.agent',
  },
]

/** Known valid node types for drag-and-drop validation (T-02-03 mitigation) */
const VALID_NODE_TYPES = new Set<string>(nodeTypeSchema.options)

/**
 * Validates that a dropped type string is one of the 4 known node types.
 * Mitigates T-02-03: Tampering via dataTransfer.
 */
// eslint-disable-next-line react-refresh/only-export-components -- type guard + component live in same module by design
export function isValidNodeType(type: string): type is NodeType {
  return VALID_NODE_TYPES.has(type)
}

export function NodePalette() {
  const { t } = useTranslation('workflows')

  return (
    <div className="flex flex-col gap-2 p-3 rounded-xl border border-border bg-card/80 backdrop-blur-sm min-w-[180px]">
      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
        {t('flow.addNode')}
      </span>
      {paletteItems.map((item) => (
        <PaletteItem
          key={item.type}
          type={item.type}
          label={t(item.labelKey)}
          icon={item.icon}
          color={item.color}
        />
      ))}
    </div>
  )
}
